import {
  Component,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../shared/icons/icon.component';
import { IconNameType } from '../../shared/icons/icon-name-type.enum';
import { RoundIconComponent } from '../../shared/round-icon/round-icon.component';
import { ConfigService } from '../services/config.service';
import { OrderType } from '../models/order-type.enum';
import { StoreStatusService} from '../services/store-status.service';
import { DeferChoice } from '../models/defer-choice.enum';
import { OrderService } from '../services/order.service';
import { OrderTimeInfo } from '../models/order-time-info.model';
import { OrderDetailsInformationComponent } from '../../shared/order-details-information/order-details-information.component';
import { OrderCreationFormComponent } from '../order-creation-form/order-creation-form.component';
import { Address } from '../models/core/address.model';
import { EditAddressComponent } from '../../shared/edit-address/edit-address.component';
import { MenuService } from '../services/menu.service';
import { AsyncButtonDirective } from '../../shared/directives/async-button.directive';
import { DateTime } from 'luxon';
import { ItemEditorService } from '../services/item-editor.service';
import { map, of, switchMap, tap } from 'rxjs';
import { ModalName, ModalService } from '../services/modal.service';
import { ItemType } from '../models/menu/item-type.enum';
import { StoreStatus } from '../models/store-status.model';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { EcommerceTriggeringArea } from '../models/ecommerce/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../models/ecommerce/ecommerce-triggering-point.enum';
import { GTMService } from '../services/gtm.service';
import { ErrorsService } from '../services/errors.service';
import { StoreChooserComponent } from '../../shared/store-chooser/store-chooser.component';
import { InitParamsService } from '../services/init-params.service';
import { AuditService } from '../services/audit.service';
import { OrderUpsellService } from '../services/order-upsell.service';
import { A11yModule } from '@angular/cdk/a11y';
import { OrderFundraiserService } from '../services/ordering/order-fundraiser.service';
import { OrderingUtilsService } from '../services/ordering/ordering-utils.service';
import { OrderTypeConfig } from '../models/order-type-config.model';

@Component({
  selector: 'cwo-order-details-modal',
  standalone: true,
  templateUrl: './order-details-modal.component.html',
  styleUrl: './order-details-modal.component.scss',
  imports: [
    CommonModule,
    IconComponent,
    RoundIconComponent,
    OrderDetailsInformationComponent,
    OrderCreationFormComponent,
    EditAddressComponent,
    AsyncButtonDirective,
    TranslocoPipe,
    StoreChooserComponent,
    A11yModule
  ],
})
export class OrderDetailsModalComponent implements OnInit {
  orderUpsellService = inject(OrderUpsellService);
  modalService = inject(ModalService);
  configService = inject(ConfigService);
  storeStatusService = inject(StoreStatusService);
  orderService = inject(OrderService);
  menuService = inject(MenuService);
  itemEditorService = inject(ItemEditorService);
  gtmService = inject(GTMService);
  errorsService = inject(ErrorsService);
  transloco = inject(TranslocoService);
  auditService = inject(AuditService);
  private initParams = inject(InitParamsService);
  private orderFundraiserService = inject(OrderFundraiserService);
  private orderingUtilsService = inject(OrderingUtilsService);
  

  activeOrderType: WritableSignal<OrderType | null> = signal(null);
  orderType = OrderType;
  schedule: WritableSignal<string> = signal('');
  stateOrderDeferTime = computed(() => this.orderService.order().deferTime ? DateTime.fromISO(this.orderService.order().deferTime!) : undefined);
  orderTypeName = computed(() => this.configService.findOrderTypeConfig(this.orderService.order().orderType)?.displayName ?? this.orderService.order().orderType ?? '');
  formattedTime = '';
  date?: DateTime;
  iconName = IconNameType;

  editingAddress?: Address;
  editingOrderType?: OrderTypeConfig;
  isSavedAddress: boolean = false;
  orderDetailsModalTitle = 'Order Details';
  orderDetailsModalMessageFromItem = this.transloco.translate('shared_components.order_details_information.info_message');

  configState = this.configService.configState;
  deferChoices: string[] = [];
  futureDates: string[] = [];
  orderTimeInfo?: OrderTimeInfo;
  submitted = false;

  expanded = false;
  storeStatus?: StoreStatus;
  embedded = signal(false);
  showOrderCreationForm = true;

  isOrderEditorOpen = () => this.modalService.stateForModal(ModalName.OrderDetails)?.showOrderEditor;

  isStoreChooserOpen = () => this.modalService.stateForModal(ModalName.OrderDetails)?.showStoreChooser;


  readonly orderCreationFormCompRef = viewChild<OrderCreationFormComponent>('orderCreationForm');

  showPrompt = computed(() =>
    this.modalService.stateForModal(ModalName.OrderDetails)?.showPrompt ?? false
  );

  constructor() {
    this.embedded.set(this.initParams.isEmbedded);
    this.storeStatus = this.storeStatusService.storeStatus();
    effect(() => {
      this.deferChoices = [];
      const tempActive = this.configState().orderTypes.find(
        (type) => type.type === this.activeOrderType()
      );

      if (
        tempActive &&
        tempActive.hasAsap &&
        this.storeStatus?.isOpen
      ) {
        this.deferChoices.push(String(tempActive.asapLabel) ?? String(DeferChoice.Asap));
      }
      if (tempActive && tempActive.allowWebDeferredOrders) {
        this.deferChoices.push(String(DeferChoice.Today));
      }
      const tempDeferredDayTimes =
        this.storeStatus?.deferredDayTimes;
      if (tempDeferredDayTimes) {
        this.futureDates = Object.keys(tempDeferredDayTimes);
        this.deferChoices.push(String(DeferChoice.Future));
      }
    });
  }

  ngOnInit(): void {
    const order = this.orderService.order();
    if (!order.dummy) {
      const convertedObj = this.orderService.getFormattedDateStatus (
        order.deferTime ?? ''
      );
      if (order.deferTime && convertedObj) {
        this.schedule.set(convertedObj.dateStatus);
        this.formattedTime = convertedObj.formattedTime;
        this.date = convertedObj.date;
      } else {
        if(order.isSelfOrderSource || this.storeStatus?.isOpen){
          this.schedule.set(DeferChoice.Asap);
        } else {
          this.schedule.set('None');
        }
      }
    }
  }


  cancelModal() {
    this.modalService.closeModal(ModalName.OrderDetails, { canceled: true });
  }
  storeStatusChange(storeStatus: StoreStatus) {
    this.storeStatus = storeStatus;
  }

  handleSuccess(force = true) {
    this.storeStatusService.updateBannerStatus();
    this.storeStatusService.setStoreStatus(this.storeStatus);
    this.closeModal(force);
  }
  
  closeModal(force = true) {
    if (!force) {
      const stateForModal = this.modalService.stateForModal(ModalName.OrderDetails);

      if(this.menuService.pendingItem()?.type === ItemType.FUNDRAISER) {
        this.modalService.closeModal(ModalName.OrderDetails);

        const panelItem = this.menuService.pendingItem();
        if(panelItem) {
          this.orderingUtilsService.addFundraiserToOrder(panelItem!, false).subscribe();
        }

        return;
      }

      if (this.menuService.pendingItem()) {
        if (this.menuService.pendingItem()?.type === ItemType.SPECIAL || this.menuService.pendingItem()?.type === ItemType.Special) {
          this.menuService.getOrderableSpecialItemFromUrl(this.menuService.pendingItem()!.links).subscribe({
            next: () => {
              this.modalService.closeModal(ModalName.OrderDetails).subscribe({
                complete: () => {
                  this.modalService.openModal(ModalName.SpecialItemEditor, {triggeringPoint: stateForModal.triggeringPoint, triggeringArea: stateForModal.triggeringArea});
                }
              });
            },
            error: (err) => {
              const parsedError = this.errorsService.parseErrorResponse(err);
              const errorMessage = parsedError.allErrors.length ? parsedError.allErrors[0] : undefined;
              this.modalService.closeModal(ModalName.OrderDetails).subscribe(() => {
                this.modalService.openGenericErrorModal({
                  modalMessage: errorMessage,
                });
              });
            }
          });
        } else {
          this.menuService
            .getOrderableItemFromUrl(this.menuService.pendingItem()!, stateForModal.triggeringPoint, stateForModal.triggeringArea).
            pipe(
              switchMap(() => {
                const orderableItem = this.itemEditorService.editingItem()?.orderableItem;
                const orderedItem = this.itemEditorService._editingItem()?.orderedItem;
                
                if (!orderableItem || !orderedItem) {
                  return of(null);
                }

                return this.orderUpsellService.getUpsells(orderableItem, orderedItem).pipe(
                  map(() => orderedItem)
                );
              })
            )
            .subscribe({
              next: () => {
                if (this.menuService.pendingItem()?.itemWithIngredientChoices || this.orderService.hasMultipleSizePrices(this.menuService.pendingItem()) || stateForModal.forceOpenModal) {
                  this.modalService.closeModal(ModalName.OrderDetails).subscribe({
                    next: () => {
                      this.modalService.openModal(ModalName.ItemEditor);                    
                    }
                  });
                } else {
                  const orderedItem = this.itemEditorService._editingItem()?.orderedItem;
                  this.orderService.addItemToOrder(orderedItem!).pipe(
                tap((orderedItem) => {
                    this.gtmService.pushAddToCartEvent({
                      order: this.orderService.order(),
                      items: [orderedItem],
                      triggeringArea: EcommerceTriggeringArea.MY_ORDERS_PAGE,
                      triggeringPoint: EcommerceTriggeringPoint.ADD_ITEM_BUTTON,
                    });
                }),
                switchMap(() => this.orderService.fetchOrder())).subscribe();
                  this.modalService.closeModal();
                }
                this.menuService.clearPendingItem();
            },
              error: (err) => {
                const parsedError = this.errorsService.parseErrorResponse(err);
                const errorMessage = parsedError.allErrors.length ? parsedError.allErrors[0] : undefined;
                if (errorMessage && this.errorsService.isStockLimitError(errorMessage)) {
                  const maxCountdown = this.errorsService.getAvailableQuantityFromError(errorMessage);
                  if (maxCountdown !== undefined) {
                    const modalMessage = maxCountdown === 0
                      ? this.transloco.translate('modals.cart.out_of_stock')
                      : `${this.transloco.translate('modals.cart.max_availability', { count: maxCountdown })}.`;
                    this.modalService.closeModal().subscribe(() => {
                      this.modalService.openGenericErrorModal({ modalMessage });
                    });
                    return;
                  }            
                }
                this.modalService.closeModal().subscribe(() => {
                  this.modalService.openGenericErrorModal({
                    modalMessage: errorMessage,
                  }); 
                });
              }
            });
        }
      } else {
        this.modalService.closeModal(ModalName.OrderDetails);
      }
    } else {
      this.modalService.closeModal(ModalName.OrderDetails, { canceled: true });
    }
  }

  editAddress(value: {
    address?: Address;
    isSaved: boolean;
    orderType?: OrderTypeConfig;
  }) {
    this.orderDetailsModalTitle = `Edit "${value.address?.name}"`;
    this.editingAddress = value.address;
    this.isSavedAddress = value.isSaved;
    this.editingOrderType = value.orderType;
  }

  backToOrderDetails(isAddressSaved = false) {
    if(this.isStoreChooserOpen()){
      if(this.orderService.order().isInitialized){
        this.modalService.updateModalState(ModalName.OrderDetails, {
          showOrderEditor: false, showStoreChooser: false
        });
      } else {
        this.modalService.updateModalState(ModalName.OrderDetails, {
          showOrderEditor: true, showStoreChooser: false
        });
      }
      return;
    }
    this.editingAddress = undefined;
    this.editingOrderType = undefined;
    this.orderDetailsModalTitle = 'Order Details';
    this.isSavedAddress = false;
    if(!isAddressSaved) {
      this.orderCreationFormCompRef()?.updateOrderAddress();
    }
  }

  handleDeliveryInstructionExpanded(value: boolean) {
    this.expanded = value;
  }

  onSubmit() {
    this.auditService.createAudit(() => 'Done button clicked on Order Details modal');
    const orderCreationFormCompRef = this.orderCreationFormCompRef();
    if (orderCreationFormCompRef) {
      orderCreationFormCompRef.onSubmit();
    }
  }
}
